import { mapErrorToUserMessage } from '@repo/core';
/**
 * ─── HRIS Payslip Viewer ─────────────────────────────────────
 * View individual payslips with full earnings + deductions breakdown.
 * Print-ready format with employee info and period details.
 */

import { useEffect, useState, useCallback } from 'react'
import { Card, CardHeader, Badge, Button } from '@repo/ui-core'
import { payrollRepo, employeeRepo } from '../../lib/db'
import { FileText, Printer, ChevronDown, ChevronUp, Search, Download } from 'lucide-react'

interface PayslipView {
  id: string
  employeeId: string
  employeeName: string
  department: string
  periodStart: number
  periodEnd: number
  basicPay: number
  overtimePay: number
  nightDifferential: number
  holidayPay: number
  grossPay: number
  sssContribution: number
  philhealthContribution: number
  pagibigContribution: number
  withholdingTax: number
  otherDeductions: number
  totalDeductions: number
  netPay: number
  status: string
  createdAt: number
}

export function HrisPayslipPage() {
  const [payslips, setPayslips] = useState<PayslipView[]>([])
  const [loading, setLoading] = useState(true)
  const [expandedId, setExpandedId] = useState<string | null>(null)
  const [search, setSearch] = useState('')

  const loadPayslips = useCallback(async () => {
    setLoading(true)
    try {
      const res = await payrollRepo.findMany({
        page: 1, pageSize: 500,
        sort: [{ field: 'createdAt', direction: 'desc' }],
      })
      setPayslips('items' in res ? res.items : [])
    } catch (e) { console.error(e); alert(mapErrorToUserMessage(e).userMessage) }
    finally { setLoading(false) }
  }, [])

  useEffect(() => { loadPayslips() }, [loadPayslips])

  const filtered = search
    ? payslips.filter(p => {
        const q = search.toLowerCase()
        const name = (p.employeeName ?? '').toLowerCase()
        const dept = (p.department ?? '').toLowerCase()
        return name.includes(q) || dept.includes(q)
      })
    : payslips

  const totalNet = filtered.reduce((s, p) => s + (p.netPay ?? 0), 0)
  const totalGross = filtered.reduce((s, p) => s + (p.grossPay ?? 0), 0)

  function handlePrint(p: PayslipView) {
    const periodStr = p.periodStart
      ? `${new Date(p.periodStart).toLocaleDateString()} – ${new Date(p.periodEnd).toLocaleDateString()}`
      : 'N/A'
    const win = window.open('', '_blank', 'width=700,height=800')
    if (!win) return
    win.document.write(`
      <html><head><title>Payslip - ${p.employeeName}</title>
      <style>
        body { font-family: Arial, sans-serif; padding: 40px; color: #1a1a1a; }
        h2 { margin: 0 0 4px 0; font-size: 20px; }
        .sub { color: #666; font-size: 13px; margin: 0 0 20px 0; }
        table { width: 100%; border-collapse: collapse; margin-bottom: 16px; }
        th, td { padding: 8px 12px; text-align: left; border-bottom: 1px solid #e5e5e5; font-size: 13px; }
        th { background: #f9fafb; color: #6b7280; font-weight: 600; text-transform: uppercase; font-size: 11px; }
        .right { text-align: right; }
        .bold { font-weight: bold; }
        .total { font-size: 15px; border-top: 2px solid #1a1a1a; }
        .green { color: #059669; }
        .red { color: #dc2626; }
        @media print { body { padding: 20px; } }
      </style></head><body>
      <h2>Payslip</h2>
      <p class="sub">${p.employeeName} — ${p.department ?? 'N/A'} — Period: ${periodStr}</p>
      <table>
        <tr><th colspan="2">Earnings</th></tr>
        <tr><td>Basic Pay</td><td class="right">₱${(p.basicPay ?? 0).toLocaleString()}</td></tr>
        <tr><td>Overtime Pay</td><td class="right">₱${(p.overtimePay ?? 0).toLocaleString()}</td></tr>
        <tr><td>Night Differential</td><td class="right">₱${(p.nightDifferential ?? 0).toLocaleString()}</td></tr>
        <tr><td>Holiday Pay</td><td class="right">₱${(p.holidayPay ?? 0).toLocaleString()}</td></tr>
        <tr class="bold"><td>Gross Pay</td><td class="right bold">₱${(p.grossPay ?? 0).toLocaleString()}</td></tr>
      </table>
      <table>
        <tr><th colspan="2">Deductions</th></tr>
        <tr><td>SSS Contribution</td><td class="right">₱${(p.sssContribution ?? 0).toLocaleString()}</td></tr>
        <tr><td>PhilHealth Contribution</td><td class="right">₱${(p.philhealthContribution ?? 0).toLocaleString()}</td></tr>
        <tr><td>Pag-IBIG Contribution</td><td class="right">₱${(p.pagibigContribution ?? 0).toLocaleString()}</td></tr>
        <tr><td>Withholding Tax</td><td class="right">₱${(p.withholdingTax ?? 0).toLocaleString()}</td></tr>
        <tr><td>Other Deductions</td><td class="right">₱${(p.otherDeductions ?? 0).toLocaleString()}</td></tr>
        <tr class="bold"><td>Total Deductions</td><td class="right bold red">₱${(p.totalDeductions ?? 0).toLocaleString()}</td></tr>
      </table>
      <table>
        <tr class="total bold"><td>NET PAY</td><td class="right bold green">₱${(p.netPay ?? 0).toLocaleString()}</td></tr>
      </table>
      <p style="font-size:11px;color:#999;margin-top:20px">Generated by CoopERP HRIS — ${new Date().toLocaleDateString()}</p>
      </body></html>
    `)
    win.document.close()
    setTimeout(() => win.print(), 300)
  }

  const expanded = payslips.find(p => p.id === expandedId)

  return (
    <div className="p-6">
      <div className="mb-6 flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Payslips</h2>
          <p className="mt-1 text-sm text-gray-500">View and print individual employee payslips with full breakdown</p>
        </div>
      </div>

      {/* Summary Stats */}
      <div className="mb-6 grid grid-cols-2 gap-3 sm:grid-cols-4">
        {[
          ['Total Payslips', filtered.length, 'blue'],
          ['Total Gross', `₱${totalGross.toLocaleString()}`, 'amber'],
          ['Total Net', `₱${totalNet.toLocaleString()}`, 'green'],
          ['Avg Net Pay', filtered.length > 0 ? `₱${Math.round(totalNet / filtered.length).toLocaleString()}` : '₱0', 'purple'],
        ].map(([label, value, color]) => (
          <div key={label as string} className={`rounded-xl border border-${color}-200 bg-${color}-50 p-4`}>
            <p className={`text-xs text-${color}-600`}>{label as string}</p>
            <p className={`mt-1 text-lg font-bold text-${color}-700`}>{value as string}</p>
          </div>
        ))}
      </div>

      {/* Search */}
      <div className="mb-4 max-w-sm">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
          <input value={search} onChange={e => setSearch(e.target.value)}
            placeholder="Search by employee name..."
            className="w-full rounded-lg border border-gray-300 py-2 pl-10 pr-3 text-sm" />
        </div>
      </div>

      {/* Payslip List */}
      <Card>
        <CardHeader title="Payslip Records" description={`${filtered.length} payslips`} />
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200 text-sm">
            <thead className="bg-gray-50">
              <tr>
                {['Employee', 'Dept', 'Period', 'Gross', 'Deductions', 'Net Pay', 'Status', 'Actions'].map(h => (
                  <th key={h} className="px-3 py-2.5 text-left text-xs font-medium uppercase text-gray-500">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 bg-white">
              {loading ? (
                <tr><td colSpan={8} className="py-8 text-center text-gray-400">Loading...</td></tr>
              ) : filtered.length === 0 ? (
                <tr><td colSpan={8} className="py-8 text-center text-gray-400">No payslips yet. Run payroll first.</td></tr>
              ) : filtered.map(p => (
                <tr key={p.id} className="hover:bg-gray-50">
                  <td className="px-3 py-2.5 font-medium text-gray-900">{p.employeeName ?? p.employeeId?.slice(0, 8)}</td>
                  <td className="px-3 py-2.5 text-gray-500 text-xs">{p.department ?? '—'}</td>
                  <td className="px-3 py-2.5 text-xs text-gray-500">
                    {p.periodStart ? new Date(p.periodStart).toLocaleDateString() : '—'}
                  </td>
                  <td className="px-3 py-2.5 text-right font-medium">₱{(p.grossPay ?? 0).toLocaleString()}</td>
                  <td className="px-3 py-2.5 text-right text-red-600">₱{(p.totalDeductions ?? 0).toLocaleString()}</td>
                  <td className="px-3 py-2.5 text-right font-bold text-green-700">₱{(p.netPay ?? 0).toLocaleString()}</td>
                  <td className="px-3 py-2.5"><Badge color="green">{p.status ?? 'processed'}</Badge></td>
                  <td className="px-3 py-2.5">
                    <div className="flex gap-1">
                      <button onClick={() => setExpandedId(expandedId === p.id ? null : p.id)}
                        className="rounded p-1 hover:bg-gray-100" title="View">
                        {expandedId === p.id ? <ChevronUp className="h-4 w-4 text-blue-600" /> : <ChevronDown className="h-4 w-4 text-blue-600" />}
                      </button>
                      <button onClick={() => handlePrint(p)} className="rounded p-1 hover:bg-gray-100" title="Print">
                        <Printer className="h-4 w-4 text-gray-600" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>

      {/* Expanded Detail */}
      {expanded && (
        <div className="mt-4 rounded-xl border border-gray-200 bg-white p-6">
          <div className="mb-4 flex items-center justify-between">
            <div>
              <h3 className="text-lg font-semibold text-gray-900">{expanded.employeeName}</h3>
              <p className="text-sm text-gray-500">
                {expanded.department} — Period: {expanded.periodStart ? new Date(expanded.periodStart).toLocaleDateString() : 'N/A'} – {expanded.periodEnd ? new Date(expanded.periodEnd).toLocaleDateString() : 'N/A'}
              </p>
            </div>
            <Button onClick={() => handlePrint(expanded)} icon={<Printer className="h-4 w-4" />} variant="secondary">
              Print Payslip
            </Button>
          </div>

          <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
            {/* Earnings */}
            <div>
              <h4 className="mb-3 text-sm font-semibold uppercase text-gray-500">Earnings</h4>
              <div className="space-y-2">
                {[
                  ['Basic Pay', expanded.basicPay],
                  ['Overtime Pay', expanded.overtimePay],
                  ['Night Differential', expanded.nightDifferential],
                  ['Holiday Pay', expanded.holidayPay],
                ].map(([label, val]) => (
                  <div key={label} className="flex justify-between rounded-lg bg-gray-50 px-4 py-2">
                    <span className="text-sm text-gray-600">{label}</span>
                    <span className="text-sm font-medium">₱{((val as number) ?? 0).toLocaleString()}</span>
                  </div>
                ))}
                <div className="flex justify-between rounded-lg bg-green-50 px-4 py-2 border border-green-200">
                  <span className="text-sm font-semibold text-green-700">Gross Pay</span>
                  <span className="text-sm font-bold text-green-700">₱{(expanded.grossPay ?? 0).toLocaleString()}</span>
                </div>
              </div>
            </div>

            {/* Deductions */}
            <div>
              <h4 className="mb-3 text-sm font-semibold uppercase text-gray-500">Deductions</h4>
              <div className="space-y-2">
                {[
                  ['SSS Contribution', expanded.sssContribution],
                  ['PhilHealth Contribution', expanded.philhealthContribution],
                  ['Pag-IBIG Contribution', expanded.pagibigContribution],
                  ['Withholding Tax', expanded.withholdingTax],
                  ['Other Deductions', expanded.otherDeductions],
                ].map(([label, val]) => (
                  <div key={label} className="flex justify-between rounded-lg bg-gray-50 px-4 py-2">
                    <span className="text-sm text-gray-600">{label}</span>
                    <span className="text-sm font-medium">₱{((val as number) ?? 0).toLocaleString()}</span>
                  </div>
                ))}
                <div className="flex justify-between rounded-lg bg-red-50 px-4 py-2 border border-red-200">
                  <span className="text-sm font-semibold text-red-700">Total Deductions</span>
                  <span className="text-sm font-bold text-red-700">₱{(expanded.totalDeductions ?? 0).toLocaleString()}</span>
                </div>
              </div>
            </div>
          </div>

          {/* Net Pay */}
          <div className="mt-6 flex justify-between rounded-xl bg-blue-600 px-6 py-4 text-white">
            <span className="text-lg font-semibold">NET PAY</span>
            <span className="text-2xl font-bold">₱{(expanded.netPay ?? 0).toLocaleString()}</span>
          </div>
        </div>
      )}
    </div>
  )
}
